/**
 * SchoolFacultyWorkspace — everything about one Faculty in one place:
 * Overview · Courses · Subjects · Departments. An admin can create a course,
 * add or attach subjects, run academic departments and clean up orphaned
 * subjects without leaving this screen.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Plus, Loader2, Users, BookOpen, GraduationCap, CalendarClock, ClipboardList,
  Library, Building2, Trash2, Link2, Unlink, AlertTriangle, Pencil, Search,
} from 'lucide-react';

const Empty = ({ children, testid }) => (
  <p className="py-8 text-center text-sm text-slate-500" data-testid={testid}>{children}</p>
);

/* ── New / edit course, pre-linked to the Faculty ─────────────────── */
export const FacultyCourseDialog = ({ open, onOpenChange, tenantId, faculty }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: '', code: '', award: 'diploma', duration_years: 1, description: '',
  });

  const { data: meta } = useQuery({
    queryKey: ['school-programmes-all', tenantId],
    queryFn: async () => (await api.get(`/school/tenants/${tenantId}/programmes`)).data,
    enabled: open,
    retry: false,
  });
  const awards = meta?.awards || ['certificate', 'diploma', 'degree'];

  const save = useMutation({
    mutationFn: async () => (await api.post(
      `/school/tenants/${tenantId}/faculties/${faculty.id}/programmes`, form)).data,
    onSuccess: () => {
      toast.success('Course created and linked to this Faculty');
      queryClient.invalidateQueries({ queryKey: ['school-faculties', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['faculty-overview', tenantId, faculty.id] });
      queryClient.invalidateQueries({ queryKey: ['school-programmes-all', tenantId] });
      setForm({ name: '', code: '', award: 'diploma', duration_years: 1, description: '' });
      onOpenChange(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!faculty) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="faculty-new-course-dialog">
        <DialogHeader>
          <DialogTitle>New course in {faculty.name}</DialogTitle>
          <DialogDescription>
            Saved here it appears under this Faculty straight away — no trip to Admissions.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Course name *</Label>
            <Input value={form.name} autoFocus data-testid="course-name-input"
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Bachelor of Theology" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Code *</Label>
              <Input value={form.code} data-testid="course-code-input"
                     onChange={(e) => setForm({ ...form, code: e.target.value })}
                     placeholder="BTH" />
            </div>
            <div className="space-y-1.5">
              <Label>Award</Label>
              <select value={form.award} data-testid="course-award-select"
                      onChange={(e) => setForm({ ...form, award: e.target.value })}
                      className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                {awards.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Duration (years)</Label>
              <Input type="number" min={0.5} step={0.5} value={form.duration_years}
                     data-testid="course-duration-input"
                     onChange={(e) => setForm({ ...form, duration_years: Number(e.target.value) })} />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input value={form.description} data-testid="course-description-input"
                     onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => save.mutate()} data-testid="course-save-btn"
                    disabled={save.isPending || !form.name.trim() || form.code.trim().length < 2}>
              {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create course
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ── Curriculum builder ───────────────────────────────────────────── */
export const CurriculumDialog = ({ open, onOpenChange, tenantId, programme }) => {
  const queryClient = useQueryClient();
  const [target, setTarget] = useState('');   // level id being filled
  const [picked, setPicked] = useState([]);

  const { data, isLoading } = useQuery({
    queryKey: ['programme-curriculum', tenantId, programme?.id],
    queryFn: async () => (await api.get(
      `/school/tenants/${tenantId}/programmes/${programme.id}/curriculum`)).data,
    enabled: open && !!programme?.id,
    staleTime: 0,
  });
  const levels = data?.levels || [];
  const pool = data?.pool || { departments: [], unassigned: [] };

  const addLevel = useMutation({
    mutationFn: async () => (await api.post(
      `/school/tenants/${tenantId}/programmes/${programme.id}/levels`,
      { name: `Year ${levels.length + 1}`, order: levels.length + 1 })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programme-curriculum', tenantId, programme.id] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const addSubjects = useMutation({
    mutationFn: async () => (await api.post(
      `/school/tenants/${tenantId}/levels/${target}/subjects`, { subject_ids: picked })).data,
    onSuccess: (res) => {
      toast.success(`${res.added} subject(s) added to this year`);
      if ((res.already_in_year || []).length) {
        toast.message(`Already in this year: ${res.already_in_year.join(', ')}`);
      }
      setPicked([]);
      queryClient.invalidateQueries({ queryKey: ['programme-curriculum', tenantId, programme.id] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const removeModule = useMutation({
    mutationFn: async (moduleId) => (await api.delete(
      `/school/tenants/${tenantId}/modules/${moduleId}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programme-curriculum', tenantId, programme.id] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!programme) return null;
  const groups = [...pool.departments,
                  ...(pool.unassigned.length ? [{ id: '', name: 'No Department', subjects: pool.unassigned }] : [])];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="curriculum-dialog">
        <DialogHeader>
          <DialogTitle>Curriculum · {programme.name}</DialogTitle>
          <DialogDescription>
            Pull this Faculty's subjects into each year of study. A subject may appear in more than
            one year (two-part modules are fine).
          </DialogDescription>
        </DialogHeader>

        {isLoading ? <Skeleton className="h-52 w-full" /> : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Years of study ({levels.length})
              </p>
              <Button size="sm" variant="outline" onClick={() => addLevel.mutate()}
                      disabled={addLevel.isPending} data-testid="curriculum-add-year-btn">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add year
              </Button>
            </div>

            {levels.length === 0 ? (
              <Empty testid="curriculum-no-years">Add the first year of study to begin.</Empty>
            ) : levels.map((lv) => (
              <div key={lv.id} className="rounded-xl border border-slate-200 p-3"
                   data-testid={`curriculum-level-${lv.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-slate-800">{lv.name}</p>
                  <Button size="sm" variant={target === lv.id ? 'default' : 'outline'}
                          onClick={() => { setTarget(lv.id); setPicked([]); }}
                          data-testid={`curriculum-pull-${lv.id}`}>
                    <Library className="h-3.5 w-3.5 mr-1.5" /> Pull from Faculty
                  </Button>
                </div>
                {lv.subjects.length === 0 ? (
                  <p className="py-3 text-xs text-slate-400">No subjects in this year yet.</p>
                ) : (
                  <div className="mt-2 divide-y divide-slate-100">
                    {lv.subjects.map((m) => (
                      <div key={m.module_id} className="flex items-center justify-between py-1.5 text-sm"
                           data-testid={`curriculum-subject-${m.module_id}`}>
                        <span className="text-slate-700">
                          {m.name} <span className="font-mono text-xs text-slate-400">{m.code}</span>
                          {m.is_elective && <Badge variant="outline" className="ml-2 text-[9px]">elective</Badge>}
                          {m.department && <span className="ml-2 text-[10px] text-slate-400">{m.department.name}</span>}
                        </span>
                        <Button size="icon" variant="ghost" className="h-7 w-7"
                                onClick={() => removeModule.mutate(m.module_id)}
                                data-testid={`curriculum-remove-${m.module_id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-slate-400" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {target && (
              <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3"
                   data-testid="curriculum-picker">
                <p className="text-sm font-medium text-indigo-900">
                  Choose subjects for {levels.find((l) => l.id === target)?.name}
                </p>
                {groups.length === 0 ? (
                  <Empty testid="curriculum-pool-empty">
                    This Faculty has no subjects yet — add some on the Subjects tab first.
                  </Empty>
                ) : groups.map((g) => (
                  <div key={g.id || 'none'} className="mt-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                      {g.name}
                    </p>
                    <div className="mt-1 grid gap-1 sm:grid-cols-2">
                      {g.subjects.map((sub) => {
                        const on = picked.includes(sub.id);
                        return (
                          <label key={sub.id} data-testid={`curriculum-pick-${sub.id}`}
                                 className={`flex items-center gap-2 rounded-lg border bg-white px-2 py-1.5 text-sm cursor-pointer ${
                                   on ? 'border-indigo-400' : 'border-slate-200'}`}>
                            <input type="checkbox" checked={on}
                                   onChange={() => setPicked(on ? picked.filter((x) => x !== sub.id) : [...picked, sub.id])} />
                            <span className="text-slate-700">{sub.name}</span>
                            <span className="font-mono text-[10px] text-slate-400">{sub.code}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <div className="mt-3 flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => { setTarget(''); setPicked([]); }}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={() => addSubjects.mutate()}
                          disabled={addSubjects.isPending || picked.length === 0}
                          data-testid="curriculum-add-subjects-btn">
                    {addSubjects.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                    Add {picked.length || ''} to year
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ── Departments ──────────────────────────────────────────────────── */
const DepartmentDialog = ({ open, onOpenChange, tenantId, faculty, editing }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: '', code: '', description: '', head_team_member_id: '', head_name: '', head_email: '',
  });

  React.useEffect(() => {
    setForm({
      name: editing?.name || '', code: editing?.code || '', description: editing?.description || '',
      head_team_member_id: editing?.head_team_member_id || '',
      head_name: editing?.head_name || '', head_email: editing?.head_email || '',
    });
  }, [editing?.id, open]);

  const { data: team } = useQuery({
    queryKey: ['school-team', tenantId],
    queryFn: async () => (await api.get(`/school/tenants/${tenantId}/team/members`)).data,
    enabled: open,
    retry: false,
  });
  const staff = team?.items || team || [];

  const save = useMutation({
    mutationFn: async () => (editing
      ? await api.put(`/school/tenants/${tenantId}/departments/${editing.id}`, form)
      : await api.post(`/school/tenants/${tenantId}/faculties/${faculty.id}/departments`, form)).data,
    onSuccess: () => {
      toast.success(editing ? 'Department updated' : 'Department created');
      queryClient.invalidateQueries({ queryKey: ['faculty-departments', tenantId, faculty.id] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="department-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.name}` : 'New academic Department'}</DialogTitle>
          <DialogDescription>
            An academic sub-unit of {faculty?.name} — e.g. Department of Biblical Studies.
            This is not an HR staff department.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Name *</Label>
            <Input value={form.name} autoFocus data-testid="department-name-input"
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Department of Biblical Studies" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Code</Label>
              <Input value={form.code} data-testid="department-code-input"
                     onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="BIB" />
            </div>
            <div className="space-y-1.5">
              <Label>Head of Department</Label>
              <select value={form.head_team_member_id} data-testid="department-head-select"
                      onChange={(e) => setForm({ ...form, head_team_member_id: e.target.value })}
                      className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="">— someone not on the team —</option>
                {staff.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}{m.position ? ` · ${m.position}` : ''}</option>
                ))}
              </select>
            </div>
          </div>
          {!form.head_team_member_id && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Head name</Label>
                <Input value={form.head_name} data-testid="department-head-name-input"
                       onChange={(e) => setForm({ ...form, head_name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Head email</Label>
                <Input value={form.head_email} data-testid="department-head-email-input"
                       onChange={(e) => setForm({ ...form, head_email: e.target.value })} />
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <Button onClick={() => save.mutate()} data-testid="department-save-btn"
                    disabled={save.isPending || form.name.trim().length < 2}>
              {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DepartmentsTab = ({ tenantId, faculty }) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['faculty-departments', tenantId, faculty.id],
    queryFn: async () => (await api.get(
      `/school/tenants/${tenantId}/faculties/${faculty.id}/departments`)).data,
  });
  const departments = data?.items || [];

  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/school/tenants/${tenantId}/departments/${id}`)).data,
    onSuccess: () => {
      toast.success('Department deleted — its subjects stay in the Faculty');
      queryClient.invalidateQueries({ queryKey: ['faculty-departments', tenantId, faculty.id] });
      queryClient.invalidateQueries({ queryKey: ['faculty-subjects', tenantId, faculty.id] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-3" data-testid="faculty-departments-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Academic sub-units of this Faculty. Subjects can be assigned to one Department each.
        </p>
        <Button size="sm" onClick={() => { setEditing(null); setOpen(true); }}
                data-testid="add-department-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New Department
        </Button>
      </div>

      {isLoading ? <Skeleton className="h-24 w-full" /> : departments.length === 0 ? (
        <Empty testid="departments-empty">
          No Departments yet. Add one for each academic grouping inside {faculty.name}.
        </Empty>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {departments.map((d) => (
            <div key={d.id} className="rounded-xl border border-slate-200 p-3"
                 data-testid={`department-card-${d.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 truncate">{d.name}</p>
                  <p className="text-xs text-slate-400">
                    {d.code ? `${d.code} · ` : ''}{d.subjects} subject{d.subjects === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="flex gap-0.5 shrink-0">
                  <Button variant="ghost" size="icon" className="h-7 w-7"
                          onClick={() => { setEditing(d); setOpen(true); }}
                          data-testid={`department-edit-${d.id}`}>
                    <Pencil className="h-3.5 w-3.5 text-slate-400" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7"
                          onClick={() => remove.mutate(d.id)}
                          data-testid={`department-delete-${d.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-slate-400" />
                  </Button>
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-500" data-testid={`department-head-${d.id}`}>
                {d.head_name ? `Head: ${d.head_name}` : 'No Head of Department yet'}
              </p>
            </div>
          ))}
        </div>
      )}

      <DepartmentDialog open={open} onOpenChange={setOpen} tenantId={tenantId}
                        faculty={faculty} editing={editing} />
    </div>
  );
};

/* ── Subjects ─────────────────────────────────────────────────────── */
const AttachSubjectsDialog = ({ open, onOpenChange, tenantId, faculty }) => {
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState([]);

  const { data, isFetching } = useQuery({
    queryKey: ['subjects-available', tenantId, faculty.id, q],
    queryFn: async () => (await api.get(
      `/school/tenants/${tenantId}/subjects/available`,
      { params: { faculty_id: faculty.id, q } })).data,
    enabled: open,
    staleTime: 0,
  });
  const subjects = data?.items || [];

  const attach = useMutation({
    mutationFn: async () => (await api.post(
      `/school/tenants/${tenantId}/faculties/${faculty.id}/subjects/attach`,
      { subject_ids: picked })).data,
    onSuccess: (res) => {
      toast.success(`${res.attached} subject(s) attached`);
      queryClient.invalidateQueries({ queryKey: ['faculty-subjects', tenantId, faculty.id] });
      queryClient.invalidateQueries({ queryKey: ['subjects-orphaned', tenantId] });
      setPicked([]);
      onOpenChange(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="attach-subjects-dialog">
        <DialogHeader>
          <DialogTitle>Attach existing subjects</DialogTitle>
          <DialogDescription>
            Search the school-wide subject list. A subject can be shared across Faculties.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9"
                   placeholder="Search by subject name or code…"
                   data-testid="attach-subject-search" />
          </div>
          {isFetching && <p className="text-xs text-slate-400">Searching…</p>}
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {subjects.length === 0 && <Empty testid="attach-subjects-empty">No subjects match.</Empty>}
            {subjects.map((s) => {
              const on = picked.includes(s.id);
              return (
                <label key={s.id} data-testid={`attach-subject-${s.id}`}
                       className={`flex items-center gap-3 rounded-lg border px-3 py-2 text-sm ${
                         s.already_attached ? 'opacity-50' : 'cursor-pointer'} ${
                         on ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200'}`}>
                  <input type="checkbox" checked={on} disabled={s.already_attached}
                         onChange={() => setPicked(on ? picked.filter((x) => x !== s.id) : [...picked, s.id])} />
                  <span className="font-medium text-slate-800">{s.name}</span>
                  <span className="text-xs font-mono text-slate-400">{s.code}</span>
                  {s.already_attached && <span className="ml-auto text-[10px] text-emerald-600">already here</span>}
                </label>
              );
            })}
          </div>
          <div className="flex justify-end">
            <Button onClick={() => attach.mutate()} disabled={attach.isPending || picked.length === 0}
                    data-testid="attach-subjects-save-btn">
              {attach.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Attach {picked.length || ''}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SubjectsTab = ({ tenantId, faculty }) => {
  const queryClient = useQueryClient();
  const [attachOpen, setAttachOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', code: '', description: '', is_elective: false, department_id: '',
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['faculty-subjects', tenantId, faculty.id] });
    queryClient.invalidateQueries({ queryKey: ['subjects-orphaned', tenantId] });
    queryClient.invalidateQueries({ queryKey: ['faculty-departments', tenantId, faculty.id] });
  };

  const { data, isLoading } = useQuery({
    queryKey: ['faculty-subjects', tenantId, faculty.id],
    queryFn: async () => (await api.get(
      `/school/tenants/${tenantId}/faculties/${faculty.id}/subjects`)).data,
  });
  const subjects = data?.items || [];

  const { data: deptData } = useQuery({
    queryKey: ['faculty-departments', tenantId, faculty.id],
    queryFn: async () => (await api.get(
      `/school/tenants/${tenantId}/faculties/${faculty.id}/departments`)).data,
  });
  const departments = deptData?.items || [];

  const { data: orphanData } = useQuery({
    queryKey: ['subjects-orphaned', tenantId],
    queryFn: async () => (await api.get(`/school/tenants/${tenantId}/subjects/orphaned`)).data,
  });
  const orphans = orphanData?.items || [];

  const create = useMutation({
    mutationFn: async () => (await api.post(
      `/school/tenants/${tenantId}/faculties/${faculty.id}/subjects`, form)).data,
    onSuccess: () => {
      toast.success('Subject created in this Faculty');
      setForm({ name: '', code: '', description: '', is_elective: false, department_id: '' });
      invalidate();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const setDepartment = useMutation({
    mutationFn: async ({ id, department_id }) => (await api.put(
      `/school/tenants/${tenantId}/subjects/${id}/department`, { department_id })).data,
    onSuccess: () => { toast.success('Department updated'); invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const detach = useMutation({
    mutationFn: async (id) => (await api.post(
      `/school/tenants/${tenantId}/faculties/${faculty.id}/subjects/detach`,
      { subject_ids: [id] })).data,
    onSuccess: () => { toast.success('Subject removed from this Faculty'); invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const attachOrphan = useMutation({
    mutationFn: async (id) => (await api.post(
      `/school/tenants/${tenantId}/faculties/${faculty.id}/subjects/attach`,
      { subject_ids: [id] })).data,
    onSuccess: () => { toast.success('Attached to this Faculty'); invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const removeSubject = useMutation({
    mutationFn: async (id) => (await api.delete(`/school/tenants/${tenantId}/subjects/${id}`)).data,
    onSuccess: () => { toast.success('Subject deleted'); invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const clearOrphans = useMutation({
    mutationFn: async () => (await api.delete(`/school/tenants/${tenantId}/subjects/orphaned`)).data,
    onSuccess: (res) => { toast.success(`${res.deleted} orphaned subject(s) deleted`); invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-5" data-testid="faculty-subjects-tab">
      <div className="rounded-xl border border-slate-200 p-3 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm font-medium text-slate-700">Add a subject to {faculty.name}</p>
          <Button size="sm" variant="outline" onClick={() => setAttachOpen(true)}
                  data-testid="attach-existing-subject-btn">
            <Link2 className="h-3.5 w-3.5 mr-1.5" /> Attach existing subject
          </Button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Subject name *</Label>
            <Input value={form.name} data-testid="subject-name-input"
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Old Testament Survey" />
          </div>
          <div className="space-y-1.5">
            <Label>Code *</Label>
            <Input value={form.code} data-testid="subject-code-input"
                   onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="OTS101" />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Input value={form.description} data-testid="subject-description-input"
                   onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Department</Label>
            <select value={form.department_id} data-testid="subject-department-select"
                    onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                    className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="">— no Department —</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={form.is_elective} data-testid="subject-elective-checkbox"
                   onChange={(e) => setForm({ ...form, is_elective: e.target.checked })} />
            Elective subject
          </label>
          <Button onClick={() => create.mutate()} data-testid="subject-save-btn"
                  disabled={create.isPending || form.name.trim().length < 2 || !form.code.trim()}>
            {create.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create subject
          </Button>
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Subjects in this Faculty ({subjects.length})
        </p>
        {isLoading ? <Skeleton className="h-24 w-full" /> : subjects.length === 0 ? (
          <Empty testid="faculty-subjects-empty">
            No subjects here yet — create one above or attach an existing subject.
          </Empty>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-3 py-2 text-left">Subject</th>
                  <th className="px-3 py-2 text-left">Faculty</th>
                  <th className="px-3 py-2 text-left">Department</th>
                  <th className="px-3 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {subjects.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100" data-testid={`subject-row-${s.id}`}>
                    <td className="px-3 py-2">
                      <span className="font-medium text-slate-800">{s.name}</span>
                      <span className="ml-2 font-mono text-xs text-slate-400">{s.code}</span>
                      {s.is_elective && (
                        <Badge variant="outline" className="ml-2 text-[9px]"
                               data-testid={`subject-elective-${s.id}`}>elective</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500" data-testid={`subject-faculties-${s.id}`}>
                      {s.faculties.map((f) => f.name).join(', ') || '—'}
                    </td>
                    <td className="px-3 py-2">
                      <select value={s.department_id || ''}
                              onChange={(e) => setDepartment.mutate({ id: s.id, department_id: e.target.value })}
                              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"
                              data-testid={`subject-department-${s.id}`}>
                        <option value="">— none —</option>
                        {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => detach.mutate(s.id)}
                              data-testid={`subject-detach-${s.id}`}>
                        <Unlink className="h-3.5 w-3.5 mr-1 text-slate-400" /> Detach
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {orphans.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3"
             data-testid="orphaned-subjects-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm font-medium text-amber-900 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {orphans.length} orphaned subject{orphans.length === 1 ? '' : 's'} — not linked to any Faculty, Department or course
            </p>
            <Button size="sm" variant="outline" className="border-amber-300 text-amber-900"
                    onClick={() => { if (window.confirm(`Delete all ${orphans.length} orphaned subjects?`)) clearOrphans.mutate(); }}
                    data-testid="delete-all-orphans-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete all orphans
            </Button>
          </div>
          <div className="mt-2 space-y-1.5">
            {orphans.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-2 rounded-lg bg-white px-3 py-2"
                   data-testid={`orphan-subject-${s.id}`}>
                <span className="text-sm text-slate-700">
                  {s.name} <span className="font-mono text-xs text-slate-400">{s.code}</span>
                </span>
                <span className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => attachOrphan.mutate(s.id)}
                          data-testid={`orphan-attach-${s.id}`}>
                    Attach to this Faculty
                  </Button>
                  <Button size="sm" variant="ghost" className="text-rose-600"
                          onClick={() => removeSubject.mutate(s.id)}
                          data-testid={`orphan-delete-${s.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <AttachSubjectsDialog open={attachOpen} onOpenChange={setAttachOpen}
                            tenantId={tenantId} faculty={faculty} />
    </div>
  );
};

/* ── Workspace shell ──────────────────────────────────────────────── */
export const SchoolFacultyWorkspace = ({ tenantId, faculty, onClose, onManageCourses }) => {
  const [newCourse, setNewCourse] = useState(false);
  const [curriculumFor, setCurriculumFor] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ['faculty-overview', tenantId, faculty.id],
    queryFn: async () => (await api.get(
      `/school/tenants/${tenantId}/faculties/${faculty.id}/overview`)).data,
  });

  const counts = data?.counts || {};
  const tiles = useMemo(() => ([
    ['Students', counts.students, Users],
    ['Courses', counts.courses, BookOpen],
    ['Modules', counts.modules, GraduationCap],
    ['Sessions', counts.sessions, CalendarClock],
    ['Results', counts.results, ClipboardList],
  ]), [counts]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto"
                     data-testid="faculty-workspace-dialog">
        <DialogHeader>
          <DialogTitle>{faculty.name}</DialogTitle>
          <DialogDescription>
            {faculty.dean_name ? `Dean: ${faculty.dean_name}` : 'No Dean assigned yet'} · manage
            courses, subjects and departments without leaving this page.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="overview">
          <TabsList data-testid="faculty-workspace-tabs">
            <TabsTrigger value="overview" data-testid="faculty-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="courses" data-testid="faculty-tab-courses">Courses</TabsTrigger>
            <TabsTrigger value="subjects" data-testid="faculty-tab-subjects">
              <Library className="h-3.5 w-3.5 mr-1.5" /> Subjects
            </TabsTrigger>
            <TabsTrigger value="departments" data-testid="faculty-tab-departments">
              <Building2 className="h-3.5 w-3.5 mr-1.5" /> Departments
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            {isLoading ? <Skeleton className="h-40 w-full" /> : (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                  {tiles.map(([label, n, Icon]) => (
                    <div key={label} className="rounded-xl border border-slate-200 px-4 py-3"
                         data-testid={`faculty-count-${label.toLowerCase()}`}>
                      <Icon className="h-3.5 w-3.5 text-slate-400" />
                      <p className="mt-1.5 text-xl font-bold tabular-nums text-slate-900">{n ?? 0}</p>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Students</p>
                  {(data?.students || []).length === 0
                    ? <Empty testid="faculty-students-empty">No students on these courses yet.</Empty>
                    : (
                      <div className="max-h-56 space-y-1.5 overflow-y-auto">
                        {data.students.map((s) => (
                          <div key={s.id} className="flex justify-between border-b border-slate-100 py-1.5 text-sm"
                               data-testid={`faculty-student-${s.id}`}>
                            <span className="text-slate-700">{s.name}</span>
                            <span className="font-mono text-xs text-slate-400">{s.student_number}</span>
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="courses" className="mt-4">
            <div className="space-y-3" data-testid="faculty-courses-tab">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-sm text-slate-500">Courses that belong to this Faculty.</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={onManageCourses}
                          data-testid="faculty-manage-courses-btn">
                    <BookOpen className="h-3.5 w-3.5 mr-1.5" /> Attach existing
                  </Button>
                  <Button size="sm" onClick={() => setNewCourse(true)}
                          data-testid="faculty-new-course-btn">
                    <Plus className="h-4 w-4 mr-1.5" /> New Course
                  </Button>
                </div>
              </div>
              {(data?.courses || []).length === 0 ? (
                <Empty testid="faculty-courses-empty">
                  No courses yet. Create one right here — it links to this Faculty automatically.
                </Empty>
              ) : (
                <div className="space-y-1.5">
                  {data.courses.map((c) => (
                    <div key={c.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                         data-testid={`faculty-course-row-${c.id}`}>
                      <span className="text-slate-700">
                        {c.name} <span className="font-mono text-xs text-slate-400">{c.code}</span>
                      </span>
                      <span className="flex items-center gap-2 text-xs text-slate-400">
                        {c.award} · {c.levels} levels
                        <Button size="sm" variant="outline" onClick={() => setCurriculumFor(c)}
                                data-testid={`faculty-curriculum-${c.id}`}>
                          <Library className="h-3.5 w-3.5 mr-1.5" /> Curriculum
                        </Button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="subjects" className="mt-4">
            <SubjectsTab tenantId={tenantId} faculty={faculty} />
          </TabsContent>

          <TabsContent value="departments" className="mt-4">
            <DepartmentsTab tenantId={tenantId} faculty={faculty} />
          </TabsContent>
        </Tabs>

        <FacultyCourseDialog open={newCourse} onOpenChange={setNewCourse}
                             tenantId={tenantId} faculty={faculty} />
        {curriculumFor && (
          <CurriculumDialog open onOpenChange={(v) => { if (!v) setCurriculumFor(null); }}
                            tenantId={tenantId} programme={curriculumFor} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SchoolFacultyWorkspace;
